import {
  Dispatch,
  EffectCallback,
  DependencyList,
  SetStateAction,
  useState,
  useCallback,
  useEffect,
  useRef
} from 'react';

// Read + JSON-parse a value from a Web Storage area (local or session), tolerating a missing or
// malformed entry (returns undefined). Exported so imperative callers (e.g. re-hydrating a working
// copy on mount) parse stored values with the exact same plumbing as the hooks below.
export const readStorage = <T>(storage: Storage, key: string): T | undefined => {
  const raw = storage.getItem(key);
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch (e) {
    console.error('Failed to parse stored value. Error:', e.message);
    return undefined;
  }
};

// Write a JSON-serialized value to a Web Storage area. Exported so an imperative caller can seed
// storage (e.g. a brand-new draft's working copy, before its component has mounted to own the hook)
// using the exact same plumbing as the hooks below.
export const writeStorage = (storage: Storage, key: string, value: unknown): void => {
  storage.setItem(key, JSON.stringify(value));
};

// Storage-agnostic backing for useLocalStorageState / useSessionStorageState — one implementation,
// parameterized by the Storage area, so neither hook forks the read/write/clear logic.
const useWebStorageState = <T>(
  storage: Storage,
  key: string,
  init?: T | ((previous?: T) => T)
): [T | undefined, Dispatch<SetStateAction<T | undefined>>, () => void] => {
  const [state, setStateRaw] = useState<T | undefined>(() => {
    const stored = readStorage<T>(storage, key);
    if (stored !== undefined) {
      return init instanceof Function ? init(stored) : stored;
    }
    return init instanceof Function ? init() : init;
  });

  const setStorageState: Dispatch<SetStateAction<T | undefined>> = useCallback(
    val => {
      setStateRaw(oldVal => {
        const newVal = val instanceof Function ? val(oldVal) : val;
        writeStorage(storage, key, newVal);
        return newVal;
      });
    },
    [storage, key]
  );

  const clearStorageState = useCallback(() => {
    setStateRaw(undefined);
    storage.removeItem(key);
  }, [storage, key]);

  return [state, setStorageState, clearStorageState];
};

export const useLocalStorageState = <T>(
  key: string,
  init?: T | ((previous?: T) => T)
): [T | undefined, Dispatch<SetStateAction<T | undefined>>, () => void] =>
  useWebStorageState<T>(window.localStorage, key, init);

// Same contract as useLocalStorageState, backed by sessionStorage (per-tab, cleared when the tab
// closes). Used as the durability sidecar for an unsaved chart working copy (see useWorkingCopy).
export const useSessionStorageState = <T>(
  key: string,
  init?: T | ((previous?: T) => T)
): [T | undefined, Dispatch<SetStateAction<T | undefined>>, () => void] =>
  useWebStorageState<T>(window.sessionStorage, key, init);

type StateInit<T> = T | (() => T);

type MapType<T extends string, U> = Partial<Record<T, U>>;
type MapInit<T extends string, U> = StateInit<MapType<T, U>>;
type MapReturn<T extends string, U> = [
  MapType<T, U>,
  (key: T, value: U) => void,
  Dispatch<SetStateAction<MapType<T, U>>>
];

export const useMap = <Key extends string, Value>(
  init?: MapInit<Key, Value>
): MapReturn<Key, Value> => {
  const [map, setMap] = useState<MapType<Key, Value>>(init || {});

  const setVal = useCallback((key: Key, value: Value) => {
    setMap(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  return [map, setVal, setMap];
};

export const useLocalStorageMap = <Key extends string, Value>(
  localStorageKey: string,
  init: MapInit<Key, Value>
): MapReturn<Key, Value> => {
  const [localStorageState, setLocalStorageState] = useLocalStorageState(localStorageKey, init);
  const [map, setMapElem, setMap] = useMap(localStorageState);

  useEffect(() => {
    if (map) {
      setLocalStorageState(map);
    }
  }, [map, setLocalStorageState]);
  return [map, setMapElem, setMap];
};

export const useList = <T>(): [T[], Dispatch<SetStateAction<T[]>>, (item: T) => void] => {
  const [list, setList] = useState<T[]>([]);

  return [list, setList, item => setList([...list, item])];
};

export const useStateFunction = <T>(initFunc: T): [T, (func: T) => void] => {
  const [funcObj, setFuncObj] = useState({
    func: initFunc
  });
  // Stable setter ref — safe to put in useEffect dep arrays.
  const setFunc = useCallback((func: T) => setFuncObj({ func }), []);
  return [funcObj.func, setFunc];
};

type UseDebugEffect = (
  effect: EffectCallback,
  deps: DependencyList | undefined,
  label?: string
) => void;

export const useDebugEffect: UseDebugEffect = (effect, deps, label = 'anonymous') => {
  const prevDeps = useRef<typeof deps>([]);

  useEffect(() => {
    console.warn('debug effect: ', label);
    for (let i = 0; i < (deps || []).length; ++i) {
      const current = (prevDeps.current || [])[i];
      const next = (deps || [])[i];
      if (current !== next) {
        console.warn(`Diff dep ${i}:`, current, next);
      }
    }
    prevDeps.current = deps;
    return effect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};
